import { translations } from './translations';
import type { Language } from './types';

/**
 * Translates a key based on the current language
 */
export const translate = (language: Language, key: string, params?: Record<string, string | number>): string => {
  const translation = translations[language][key];
  
  if (!translation) {
    return key;
  }
  
  if (params) {
    return Object.entries(params).reduce((acc, [paramKey, paramValue]) => {
      return acc.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
    }, translation);
  }
  
  return translation;
};

/**
 * Detects the browser language and returns the supported language
 * Returns 'en' as default if browser language is not supported
 */
export const detectBrowserLanguage = (): Language => {
  // Get browser language (e.g., "en-US", "hu-HU", "hu")
  const browserLang = navigator.language || (navigator as any).userLanguage;
  
  // Check if the browser language starts with "hu"
  if (browserLang && browserLang.toLowerCase().startsWith('hu')) {
    return 'hu';
  }
  
  // Default to English for all other languages
  return 'en';
};

/**
 * Persists the language preference in localStorage
 */
export const saveLanguagePreference = (language: Language): void => {
  localStorage.setItem('language', language);
};

/**
 * Retrieves the language preference from localStorage or detects from browser
 */
export const getLanguagePreference = (): Language => {
  const storedLanguage = localStorage.getItem('language');
  
  // If a language is stored in localStorage, use that
  if (storedLanguage === 'en' || storedLanguage === 'hu') {
    return storedLanguage as Language;
  }
  
  // Otherwise, detect from browser
  return detectBrowserLanguage();
};
